import { FC, useState } from 'react';
import { Box, Button, Dialog, Stack, TextField, Typography, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { useTrans } from '@libs/i18n';
import { KeyIcon, UserPlusIcon, XIcon } from '@phosphor-icons/react';

import { CreateTenantUserDto, useCreateTenantUser } from '../api/admin-api';

import { adminButtonSx, adminColors } from './adminTheme';

interface Props {
  open: boolean;
  onClose: () => void;
  tenantId: string;
  /** Human-facing tenant slug/name, shown in the subtitle. */
  tenantName: string;
}

/** Modal that creates a Keycloak user mapped to a tenant. */
export const AddUserModal: FC<Props> = observer(({ open, onClose, tenantId, tenantName }) => {
  const theme = useTheme();
  const c = adminColors(theme);
  const { t } = useTrans();
  const createUser = useCreateTenantUser(tenantId);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const reset = () => {
    setFirstName('');
    setLastName('');
    setEmail('');
    setSubmitted(false);
  };

  const close = () => {
    reset();
    onClose();
  };

  const firstNameError = submitted && !firstName.trim();
  const lastNameError = submitted && !lastName.trim();
  const emailError = submitted && !email.trim();

  const handleSubmit = () => {
    setSubmitted(true);
    if (!firstName.trim() || !lastName.trim() || !email.trim()) return;
    const body: CreateTenantUserDto = {
      email: email.trim(),
      firstName: firstName.trim(),
      lastName: lastName.trim(),
    };
    createUser.mutate(body, { onSuccess: close });
  };

  return (
    <Dialog
      open={open}
      onClose={close}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 5, overflow: 'hidden' } }}
    >
      <Box
        sx={{
          px: 3,
          py: 2.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1.625,
          borderBottom: `1px solid ${c.border}`,
        }}
      >
        <Box
          sx={{
            width: 38,
            height: 38,
            borderRadius: 2.5,
            backgroundColor: theme.palette.grey[200],
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <UserPlusIcon size={20} color={c.azure} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 16, fontWeight: 600, color: c.ink }}>
            {t('app.admin.addUser.title')}
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: c.faint, mt: 0.125 }}>
            {t('app.admin.addUser.subtitle', { tenant: tenantName })}
          </Typography>
        </Box>
        <Button
          onClick={close}
          sx={{ minWidth: 32, width: 32, height: 32, p: 0, borderRadius: 2, backgroundColor: theme.palette.grey[200], color: c.sub }}
        >
          <XIcon size={16} />
        </Button>
      </Box>

      <Box sx={{ px: 3, py: 2.75, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Stack direction="row" spacing={1.5}>
          <TextField
            label={t('app.admin.addUser.firstName')}
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            error={firstNameError}
            size="small"
            fullWidth
          />
          <TextField
            label={t('app.admin.addUser.lastName')}
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            error={lastNameError}
            size="small"
            fullWidth
          />
        </Stack>
        <TextField
          label={t('app.admin.addUser.email')}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="user@company.sa"
          error={emailError}
          helperText={emailError ? undefined : t('app.admin.addUser.emailHint')}
          size="small"
          fullWidth
        />
        <Box
          sx={{
            display: 'flex',
            gap: 1.125,
            backgroundColor: theme.palette.grey[200],
            borderRadius: 2.5,
            p: 1.75,
          }}
        >
          <KeyIcon size={17} color={c.sub} style={{ marginTop: 1, flexShrink: 0 }} />
          <Typography sx={{ fontSize: 11.5, color: c.sub, lineHeight: 1.55 }}>
            {t('app.admin.addUser.note')}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          px: 3,
          py: 2,
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1.25,
          borderTop: `1px solid ${c.border}`,
        }}
      >
        <Button
          onClick={close}
          sx={{ ...adminButtonSx(theme).neutral, px: 2, borderRadius: 2.75 }}
        >
          {t('app.admin.addUser.cancel')}
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={createUser.isPending}
          startIcon={<UserPlusIcon size={16} />}
          sx={{ ...adminButtonSx(theme).solid, px: 2, borderRadius: 2.75 }}
        >
          {t('app.admin.addUser.submit')}
        </Button>
      </Box>
    </Dialog>
  );
});
